import { NavigationItem, NavigationItemType, GranularityOptions } from 'components/charts/granularity-navigation/types.ts'
import { Granularity } from 'types/contracts.ts'
import { ManipulateType } from 'dayjs'
import { memoize } from 'proxy-memoize'
import {
  GenerateGetVolumesQuery,
  GenerateGranularityNavigationItems,
  GenerateGranularityRange,
  GranularityRange,
  OptionalGranularity
} from 'pages/app/consumption/types.ts'
import { getArrayOfFormattedDatesBetweenDates, toBelgianTime } from 'utils/date.ts'
import { CONTRACT_STATUSES, ENERGY_TYPES } from '@boltenergy-be/shared'

/**
 * Generates the from & until value for the given params, as Belgian wall clock boundaries
 *
 * @param {string} date
 * @param {dayjs.ManipulateType} dayjsUnit
 * @returns {Pick<GranularityRange, 'from' | 'until'>}
 */
export const generateFromAndToValue = (date: string, dayjsUnit: ManipulateType): Pick<GranularityRange, 'from' | 'until'> => {
  return {
    from: toBelgianTime(date).startOf(dayjsUnit),
    until: toBelgianTime(date).endOf(dayjsUnit)
  }
}

/**
 * Generates the granularity navigation items based on the given contract
 *
 * @param {GenerateGranularityNavigationItems} params
 * @returns {GranularityOptions}
 */
export const generateGranularityNavigationItems = ({
  contract,
  granularities,
  dataDelayInDays = 0
}: GenerateGranularityNavigationItems): GranularityOptions => {
  const now = toBelgianTime()
  const isTerminated =
    contract.detail.status === CONTRACT_STATUSES.terminated && now.isAfter(toBelgianTime(contract.detail.effectiveEndDate))
  const endDate = isTerminated ? toBelgianTime(contract.detail.effectiveEndDate) : now
  const startDate = toBelgianTime(contract.detail.contractualStartDate)
  const defaultLastYear = !isTerminated && now.month() === 0 && startDate.year() < now.year()
  const comparisonDate = defaultLastYear ? now.subtract(1, 'year').set('month', 11).set('date', 31) : endDate

  // Only a dynamic tariff electricity contract has data per hour, unless the caller states otherwise
  const isDynamicTariff = contract.detail.energyType === ENERGY_TYPES.electricity && !!contract.detail.dynamicTariff
  const includeGranularity: Record<OptionalGranularity, boolean> = {
    [Granularity.DAY]: isDynamicTariff,
    [Granularity.HOUR]: isDynamicTariff,
    [Granularity.QUARTER_HOUR]: isDynamicTariff,
    ...granularities
  }

  const navigationItems: GranularityOptions = {
    [Granularity.MONTH]: {
      translationKey: 'year',
      id: Granularity.MONTH,
      type: NavigationItemType.GRANULARITY,
      periods: getArrayOfFormattedDatesBetweenDates(startDate, endDate.subtract(1, 'day').endOf('year'), 'year').map((yearISO) => ({
        label: toBelgianTime(yearISO).format('YYYY'),
        defaultSelected: toBelgianTime(yearISO).year() === comparisonDate.year(),
        value: yearISO
      }))
    } satisfies NavigationItem
  }

  if (includeGranularity[Granularity.DAY]) {
    navigationItems[Granularity.DAY] = {
      translationKey: 'month',
      id: Granularity.DAY,
      type: NavigationItemType.GRANULARITY,
      periods: getArrayOfFormattedDatesBetweenDates(startDate, endDate.endOf('month'), 'month').map((monthISO) => ({
        label: toBelgianTime(monthISO).format('MMM YYYY'),
        defaultSelected: toBelgianTime(monthISO).format('MMM YYYY') === endDate.format('MMM YYYY'),
        value: monthISO
      }))
    }
  }

  // Both the HOUR & QUARTER_HOUR view show a single day, only at a different resolution, so they share their periods
  if (includeGranularity[Granularity.HOUR] || includeGranularity[Granularity.QUARTER_HOUR]) {
    // The most recent day with data is not today for data that lags behind (e.g. the volumes)
    const defaultDay = endDate.subtract(dataDelayInDays, 'day')
    const dayPeriods = getArrayOfFormattedDatesBetweenDates(startDate, endDate.endOf('day'), 'day').map((dateISO) => ({
      label: toBelgianTime(dateISO).format('DD/MM/YYYY'),
      defaultSelected: toBelgianTime(dateISO).format('DD/MM/YYYY') === defaultDay.format('DD/MM/YYYY'),
      value: dateISO
    }))

    if (includeGranularity[Granularity.HOUR]) {
      navigationItems[Granularity.HOUR] = {
        translationKey: 'day',
        id: Granularity.HOUR,
        type: NavigationItemType.GRANULARITY,
        periods: dayPeriods
      }
    }

    if (includeGranularity[Granularity.QUARTER_HOUR]) {
      navigationItems[Granularity.QUARTER_HOUR] = {
        translationKey: 'quarterHour',
        id: Granularity.QUARTER_HOUR,
        type: NavigationItemType.GRANULARITY,
        periods: dayPeriods
      }
    }
  }

  return navigationItems
}

/**
 * Memoized variant of generateGranularityNavigationItems
 */
export const memoizedGenerateGranularityNavigationItems = memoize<GenerateGranularityNavigationItems, GranularityOptions>(
  generateGranularityNavigationItems
)

/**
 * Generates the granularity & date range for the given granularity view & selected period
 *
 * @param {GenerateGranularityRange} params
 * @returns {GranularityRange | undefined}
 */
export const generateGranularityRange = ({ view, period }: GenerateGranularityRange): GranularityRange | undefined => {
  if (!period) return undefined

  switch (view) {
    // Show data for a complete year with MONTH granularity
    case Granularity.MONTH:
      return {
        granularity: Granularity.MONTH,
        ...generateFromAndToValue(period, 'year')
      }

    // Show data for a complete month with DAY granularity
    case Granularity.DAY:
      return {
        granularity: Granularity.DAY,
        ...generateFromAndToValue(period, 'month')
      }

    // Show data for a complete day with HOUR or QUARTER_HOUR granularity
    case Granularity.HOUR:
    case Granularity.QUARTER_HOUR:
      return {
        granularity: view,
        ...generateFromAndToValue(period, 'day')
      }
  }
}

/**
 * Memoized variant of generateGranularityRange
 */
export const memoizedGenerateGranularityRange = memoize(generateGranularityRange)

/**
 * Generate the query body for the getVolumes API
 * @param {GenerateGetVolumesQuery} params
 * @returns {GranularityRange | undefined}
 */
export const generateGetVolumesQuery = ({
  currentView,
  currentPeriod,
  billingCyclePeriods
}: GenerateGetVolumesQuery): GranularityRange | undefined => {
  if (!currentPeriod) return undefined

  if (currentView === NavigationItemType.CYCLE) {
    const cycle = billingCyclePeriods?.find(({ id }) => id === currentPeriod)

    return {
      granularity: Granularity.MONTH,
      from: toBelgianTime(cycle?.startDate).startOf('day'),
      until: toBelgianTime(cycle?.endDate).endOf('day')
    }
  }

  return generateGranularityRange({ view: currentView, period: currentPeriod })
}

export const memoizedGenerateGetVolumesQuery = memoize(generateGetVolumesQuery)
